<template>
  <div>
    <section class="relative overflow-hidden pt-28 pb-14 md:pt-32 md:pb-16">
      <div class="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white dark:from-dark dark:via-dark dark:to-dark transition-colors duration-300">
        <div class="absolute -top-24 -left-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl"></div>
        <div class="absolute -bottom-24 -right-20 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl"></div>
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-4">{{ tr('Portfolio', 'Portfolio') }}</p>
          <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1] mb-4 text-gray-900 dark:text-white transition-colors duration-300">
            {{ tr('Progetti web che portano risultati reali', 'Web projects that drive real results') }}
          </h1>
          <p class="text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed mb-8 transition-colors duration-300">
            {{ tr('Una selezione essenziale dei nostri lavori migliori.', 'A curated selection of our best work.') }}
          </p>

          <div class="flex items-center justify-center">
            <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-gray-900 text-white rounded-xl font-bold text-lg transition-colors duration-300 hover:bg-gray-800">
              {{ tr('Contattaci', 'Contact us') }}
            </router-link>
          </div>
        </div>
      </div>
      <div class="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-0"></div>
    </section>

    <section id="portfolio-projects" class="pt-12 pb-10 md:pt-14 md:pb-12 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto mb-8 rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-5 transition-colors duration-300">
          <p class="text-sm md:text-[15px] text-gray-600 dark:text-gray-300 leading-relaxed text-center">
            {{ tr("I siti mostrati servono solo a dare un'idea del nostro stile e della qualita del lavoro. Ogni progetto reale nasce su misura: analizziamo attivita, identita e obiettivi, per creare un sito che non solo funziona bene, ma racconta davvero chi sei.", "The websites shown here are only meant to give you an idea of our style and work quality. Every real project is custom: we analyze your business, identity, and goals to build a site that not only performs well, but truly tells your story.") }}
          </p>
        </div>

        <div class="flex items-center justify-between mb-10">
          <div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-3">{{ tr('Portfolio', 'Portfolio') }}</p>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ tr('Progetti', 'Projects') }}</h2>
          </div>
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ projects.length }} {{ tr('siti web', 'websites') }}</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a
            v-for="project in projects"
            :key="project.title"
            :href="project.url"
            target="_blank"
            rel="noopener noreferrer"
            class="group rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] overflow-hidden hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 transition-all duration-300"
          >
            <div class="aspect-[16/10] overflow-hidden">
              <img
                :src="project.image"
                :alt="project.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div class="p-6">
              <div class="flex items-center justify-between mb-4">
                <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  {{ tr('Sito web', 'Website') }}
                </span>
                <span class="text-xs text-gray-400 dark:text-gray-500">{{ project.year }}</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ project.title }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-5 transition-colors duration-300">
                {{ project.description }}
              </p>
              <span class="inline-flex items-center text-sm font-semibold text-blue-600 dark:text-blue-400">
                {{ tr('Visita sito', 'Visit site') }}
                <span class="ml-1 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </span>
            </div>
          </a>
        </div>

        <p v-if="isLoading" class="mt-5 text-sm text-gray-500 dark:text-gray-400">{{ tr('Caricamento progetti...', 'Loading projects...') }}</p>
        <p v-else-if="!projects.length" class="mt-5 text-sm text-gray-500 dark:text-gray-400">{{ tr('Nessun progetto disponibile al momento.', 'No projects available right now.') }}</p>
      </div>
    </section>

    <section class="pt-8 pb-14 md:pt-10 md:pb-16 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between mb-4">
          <div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-3">{{ tr('Lab interno', 'Internal lab') }}</p>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ tr('Esempi', 'Examples') }}</h2>
          </div>
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ examples.length }} {{ tr('demo interne', 'internal demos') }}</span>
        </div>
        <p class="text-gray-500 dark:text-gray-400 mb-10 max-w-3xl leading-relaxed transition-colors duration-300">
          {{ tr('Questi sono siti demo creati internamente, non collegati a clienti specifici.', 'These are internal demo sites, not tied to specific clients.') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a
            v-for="example in examples"
            :key="example.title"
            :href="example.url"
            target="_blank"
            rel="noopener noreferrer"
            class="group rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] overflow-hidden hover:border-gray-400 dark:hover:border-white/[0.2] hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-white/5 hover:-translate-y-1 transition-all duration-300"
          >
            <div class="aspect-[16/10] overflow-hidden">
              <img
                :src="example.image"
                :alt="example.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div class="p-6">
              <div class="flex items-center justify-between mb-4">
                <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-200 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                  {{ tr('Esempio', 'Example') }}
                </span>
                <span class="text-xs text-gray-400 dark:text-gray-500">{{ example.year }}</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ example.title }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-5 transition-colors duration-300">
                {{ example.description }}
              </p>
              <span class="inline-flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                {{ tr('Apri esempio', 'Open example') }}
                <span class="ml-1 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </span>
            </div>
          </a>
        </div>

        <p v-if="isLoading" class="mt-5 text-sm text-gray-500 dark:text-gray-400">{{ tr('Caricamento esempi...', 'Loading examples...') }}</p>
        <p v-else-if="!examples.length" class="mt-5 text-sm text-gray-500 dark:text-gray-400">{{ tr('Nessun esempio disponibile al momento.', 'No examples available right now.') }}</p>
      </div>
    </section>

    <section class="relative py-28 md:py-36 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/website_bg.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl md:text-4xl font-bold text-white mb-6">{{ tr('Vuoi un progetto su misura per la tua attivita?', 'Need a custom project for your business?') }}</h2>
          <p class="text-lg text-white/85 mb-10 leading-relaxed">
            {{ tr('Ogni sito nasce da obiettivi concreti: brand positioning, lead generation e conversioni.', 'Every website starts from clear goals: brand positioning, lead generation, and conversions.') }}
          </p>
          <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
            {{ tr('Richiedi una consulenza', 'Request a consultation') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const tr = (it, en) => ((locale.value || '').startsWith('en') ? en : it)

const trackingCode = 'AVElLWEEFS1qC3341qy6mQ'
const apiBase = 'https://panettone.ciobai.ch/analytics/api/public'

const fallbackImage = 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1200&q=80'

const projects = ref([])
const examples = ref([])
const isLoading = ref(false)

const getLocalizedText = (item, itField, enField) => {
  if ((locale.value || '').startsWith('en')) {
    return item?.[enField] || item?.[itField] || ''
  }
  return item?.[itField] || item?.[enField] || ''
}

const mapProject = (item) => ({
  title: item.title || 'Progetto web',
  description: getLocalizedText(item, 'text', 'text_en'),
  url: item.url || '#',
  year: item.year || '',
  image: item.image_url || fallbackImage
})

const fetchPortfolioData = async () => {
  isLoading.value = true

  try {
    const [workRes, exampleRes] = await Promise.all([
      fetch(`${apiBase}/portfolio-projects?tracking_code=${trackingCode}&project_type=work&limit=6`),
      fetch(`${apiBase}/portfolio-projects?tracking_code=${trackingCode}&project_type=example&limit=6`)
    ])

    const [workJson, exampleJson] = await Promise.all([
      workRes.json(),
      exampleRes.json()
    ])

    projects.value = Array.isArray(workJson?.data) ? workJson.data.map(mapProject) : []
    examples.value = Array.isArray(exampleJson?.data) ? exampleJson.data.map(mapProject) : []
  } catch (error) {
    console.error('Portfolio API error:', error)
    projects.value = []
    examples.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchPortfolioData()
})

defineOptions({
  name: 'PortfolioPage'
})
</script>
